import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Edit, Trash2, Copy } from "lucide-react";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", content: "" });

  const load = () => api.get("/templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", content: "" }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, content: t.content }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.content.trim()) return toast.error("Preencha nome e conteúdo.");
    try {
      if (editing) await api.put(`/templates/${editing.id}`, form);
      else await api.post("/templates", form);
      toast.success("Modelo salvo!"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/templates/${id}`); toast.success("Modelo excluído."); load(); };
  const duplicate = async (id) => { await api.post(`/templates/${id}/duplicate`); toast.success("Modelo duplicado!"); load(); };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Modelos de Mensagem</h1><p className="text-sm text-slate-500 mt-1">Salve mensagens prontas com variáveis {"{nome}"}, {"{whatsapp}"}, {"{email}"}.</p></div>
        <Button onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600" data-testid="template-add-button"><Plus className="h-4 w-4 mr-2" /> Novo Modelo</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`template-card-${t.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center"><FileText className="h-4 w-4 text-emerald-600" /></div>
              <h3 className="font-semibold text-slate-800">{t.name}</h3>
            </div>
            <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-4 min-h-[80px]">{t.content}</p>
            <div className="flex gap-1 mt-4 pt-3 border-t border-slate-100">
              <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-slate-600" data-testid={`template-edit-${t.id}`}><Edit className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => duplicate(t.id)} className="text-slate-600"><Copy className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(t.id)} className="text-red-500 ml-auto"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar Modelo" : "Novo Modelo"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="template-name-input" /></div>
            <div><Label>Conteúdo</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className="mt-1.5" data-testid="template-content-input" /><p className="text-xs text-slate-400 mt-1">Variáveis: {"{nome}"} {"{whatsapp}"} {"{email}"}</p></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-emerald-500 hover:bg-emerald-600" data-testid="template-save-button">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
